import { SignalSlashIcon } from '@heroicons/react/24/outline';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { clearPageNotifications, openNotification } from '@/components/UI/DesktopNotifications';
import { ConnectionStatus } from '@/pages/(guarded)/release-center/components/ConnectionStatus';
import {
  dispatchOutdatedAlert,
  dispatchRemoveAlert,
  dispatchShowAlert,
} from '@/renderer/utils/alerts';
import { request } from '@/renderer/utils/api';
import { useInterval } from '@/renderer/utils/hooks/useInterval';
import { useIsFirstRender } from '@/renderer/utils/hooks/useIsFirstRender';
import { useNetworkState } from '@/renderer/utils/hooks/useNetworkState';
import { useRefUpdating } from '@/renderer/utils/hooks/useRefUpdating';
import { useSsr } from '@/renderer/utils/hooks/useSSr';
import { useUpdateEffect } from '@/renderer/utils/hooks/useUpdateEffect';
import { installAudioUnlock, playSound, preloadSounds } from '@/renderer/utils/play';
import { IS_SERVER } from '@/renderer/utils/ssr';
import { reportSound } from '@/renderer/utils/stat/sound';
import {
  LATE_TONE_COALESCE_MS,
  TONE_LIVE_MS,
  type ToneName,
  classifyTone,
  decideTone,
  formatLateness,
  supersedesPendingTone,
} from '@/renderer/utils/toneGate';
import { useIdleContext } from '@/renderer/utils/useIdle';
import useMe, { authToken, refreshToken, useAuthToken } from '@/renderer/utils/useMe';
import useOnlineStatus from '@/renderer/utils/useOnlineStatus';
import { usePageContext } from '@/renderer/utils/usePageContext';
import { useRasContext } from '@/renderer/utils/useRasContext';
import { useRequestFetcher } from '@/renderer/utils/useRequest';
import { useTimeout } from '@/renderer/utils/useTimeout';
import useWebSocket, { WS_BASE_URL } from '@/renderer/utils/useWebSocket2';
import { setWsActivity } from '@/renderer/utils/ws/connection';
import type { Connection } from '@/renderer/utils/ws/types';
import type { RequestSchema } from '@/types/request';

interface ConnectData {
  message: 'AUTHORIZED' | 'UNAUTHORIZED';
  versions: {
    api: string;
    apiDate: number;
    base: string;
    baseDate: number;
    client: string;
    clientDate: number;
    app: string;
    appDate: number;
    // The CLIENT_MIN_DATE floor from the server's 417 gate (0 = unset). Only a
    // client whose baked APP_DATE is below this is genuinely out-of-date; a bare
    // appDate mismatch is a routine backwards-compatible deploy (#1680).
    clientMinDate?: number;
  };
}

enum WarningLevel {
  None,
  Short,
  Long,
}

const JITTER = 1000 * Math.random();
const WAIT_LONG = 120000; // increased from 30s because we have a separate ConnectionStatusIcon now
const WAIT_SHORT = 2500;
const WAIT_START = 8000;

const APP_DATE = parseInt(`${import.meta.env.VITE_PUBLIC_ENV__APP_DATE}`, 10) || 0;

// Use a random timer 0-250ms to avoid hammering the server all at once
const _FLAG_TIMEOUT = Math.floor(Math.random() * 250);

export enum FLAG_STATE {
  // received (WS or polled)
  ACK = 4,
  // sent desktop notification
  OPENED = 5,
  // was shown alert
  READ = 6,
  // was shown message on Release Center / DMs + was not idle
  VISITED = 7,
}

interface EventItem<T extends API.DbItem, Name extends string> {
  event: Name;
  data: T;
}

interface EventFlag<T extends API.DbItem, Name extends string> {
  event: Name;
  data: {
    id?: API.Id;
    item?: T;
    flag: FLAG_STATE;
    idleFlag?: FLAG_STATE;
  };
}

export interface CustomEventMap<T extends API.DbItem = any, Name extends string = any> {
  'ras:webSocketWatcher/created': CustomEvent<EventItem<T, Name>>;
  'ras:webSocketWatcher/flag': CustomEvent<EventFlag<T, Name>>;
}

interface WebSocketWatcherEventTarget extends EventTarget {
  addEventListener<K extends keyof CustomEventMap>(
    type: K,
    listener:
      | ((ev: CustomEventMap[K]) => void)
      | Record<'handleEvent', (evt: CustomEventMap[K]) => void>
      | null,
    options?: boolean | AddEventListenerOptions,
  ): void;
  dispatchEvent<K extends keyof CustomEventMap>(ev: CustomEventMap[K]): boolean;
  removeEventListener<K extends keyof CustomEventMap>(
    type: K,
    listener:
      | ((ev: CustomEventMap[K]) => void)
      | Record<'handleEvent', (evt: CustomEventMap[K]) => void>
      | null,
    options?: boolean | EventListenerOptions,
  ): void;
}

export const webSocketWatcherEventTarget: WebSocketWatcherEventTarget = new EventTarget();

const dmFlagCacheMap = new Map<string, { state: FLAG_STATE; timer?: NodeJS.Timeout }>();

// The single late-tone slot (#2041).
//
// A reconnect catch-up hands us a whole batch of missed receipts within a few
// ticks, in no guaranteed order (useInfinitePolling dispatches one
// `messageReceipt/created` per injected row). Sounding each of them would be a
// pile-up, and sounding them in arrival order could put an older GO after a
// newer message. So a late tone is parked here for LATE_TONE_COALESCE_MS and
// only the newest receipt in that window gets to sound — exactly one tone per
// catch-up, and always the newest one.
let pendingLateTone: { createdAtMs: number; tone: ToneName; ageMs: number } | undefined;
let pendingLateToneTimer: ReturnType<typeof setTimeout> | undefined;

function clearPendingLateTone() {
  if (pendingLateToneTimer !== undefined) {
    clearTimeout(pendingLateToneTimer);
    pendingLateToneTimer = undefined;
  }
  pendingLateTone = undefined;
}

function flushLateTone() {
  pendingLateToneTimer = undefined;
  const pending = pendingLateTone;
  pendingLateTone = undefined;

  if (pending) {
    void playSound(pending.tone, { late: true, ageMs: pending.ageMs });
  }
}

/** Park a late tone, if it is newer than whatever is already parked. */
function scheduleLateTone(createdAtMs: number, tone: ToneName, ageMs: number) {
  if (!supersedesPendingTone(pendingLateTone?.createdAtMs, createdAtMs)) {
    return;
  }

  pendingLateTone = { createdAtMs, tone, ageMs };

  // Deliberately does NOT re-arm an existing timer: the wait is measured from
  // the FIRST candidate, so a long catch-up batch can't keep pushing the tone
  // further out.
  if (pendingLateToneTimer === undefined) {
    pendingLateToneTimer = setTimeout(flushLateTone, LATE_TONE_COALESCE_MS);
  }
}

/**
 * A newer receipt has landed that will not itself sound late (it played live, or
 * it is banner-only). Drop any older tone still parked, so a stale GO can never
 * sound behind a newer message.
 */
function supersedeLateTone(createdAtMs: number) {
  if (pendingLateTone && supersedesPendingTone(pendingLateTone.createdAtMs, createdAtMs)) {
    clearPendingLateTone();
  }
}

// Coalesced web-delivery flag writes (#1801). A release NOTIFY delivers a batch
// of receipts to every client at once; each used to be recorded with its own
// `POST /me/:id/flag` ~500ms later, so a batch of N receipts fanned out N write
// requests and 503'd the pool under load — silently losing the web delivery
// record. Accumulate a tick's worth of receipt flags and send them as ONE bulk
// `POST /me/flag`. Writes stay per-receipt monotonic (highest state wins, and
// the server only advances, never downgrades).
const WEB_FLAG_FLUSH_MS = 750;
const pendingWebFlags = new Map<API.Id, number>(); // id -> highest state to write
const sentWebFlags = new Map<API.Id, number>(); // id -> highest state already sent
let webFlagFlushTimer: ReturnType<typeof setTimeout> | undefined;

function flushWebFlags() {
  webFlagFlushTimer = undefined;

  if (!pendingWebFlags.size) {
    return;
  }

  const flags = [...pendingWebFlags].map(([id, web]) => ({ id, web }));
  pendingWebFlags.clear();

  // Optimistically remember what we're sending so repeats de-duplicate; keep the
  // sent map bounded.
  for (const { id, web } of flags) {
    const prev = sentWebFlags.get(id);
    if (prev === undefined || web > prev) {
      sentWebFlags.set(id, web);
    }
  }
  if (sentWebFlags.size > 200) {
    for (const key of sentWebFlags.keys()) {
      sentWebFlags.delete(key);
      if (sentWebFlags.size <= 200) {
        break;
      }
    }
  }

  request('/api/v1/messageReceipt/me/flag' as any, {
    method: 'POST',
    body: { data: { flags } },
  }).catch(() => {
    // The server never recorded these — forget them so the undelivered-message
    // resend can re-trigger an ACK later.
    for (const { id } of flags) {
      sentWebFlags.delete(id);
    }
  });
}

// Queue one receipt's web-delivery flag into the next coalesced bulk write.
function queueWebFlag(id: API.Id | undefined, web: number) {
  if (!id || !(web > 0)) {
    return;
  }
  const alreadySent = sentWebFlags.get(id);
  if (alreadySent !== undefined && web <= alreadySent) {
    return;
  }
  const queued = pendingWebFlags.get(id);
  if (queued === undefined || web > queued) {
    pendingWebFlags.set(id, web);
  }
  if (webFlagFlushTimer === undefined) {
    webFlagFlushTimer = setTimeout(flushWebFlags, WEB_FLAG_FLUSH_MS);
  }
}

/**
 * Is the viewer already looking at the support conversation this DM belongs to?
 *
 * A support conversation is keyed on its non-staff participant, so the open page
 * is `/support-chat/<customerId>` for staff and the bare `/support-chat` for a
 * regular member (who only ever has the one thread). When we're already on that
 * page a new message is right there on screen, so it must not also pop an in-app
 * toast — the same "am I on this conversation?" notion the Nav unread badge uses.
 *
 * This is decided from the pathname and the DM's participant ids alone, on
 * purpose — independent of the role / `receiverUserId` direction classification
 * in `onWatcherDmCreated` (which only builds the deep-link, submessage and
 * sound). That classification can misfire: if `me` (hence `isStaff`) is briefly
 * unset right after a client navigation into a conversation, the direction logic
 * is skipped, `dmPath` stays the bare `/support-chat`, and the old
 * `pathname !== dmPath` gate let the toast through for an admin sitting in that
 * very chat. Matching either participant id keeps suppression correct no matter
 * what the role/direction came out to.
 */
export function viewingDmConversation(
  pathname: string,
  // `receiverUserId` is typed non-null on the model, but the server sends it null
  // on the member→admin fan-out, so accept the real (nullable) shape.
  dm: { userId?: API.Id | null; receiverUserId?: API.Id | null },
  isStaff: boolean,
): boolean {
  if (dm.userId != null && pathname === `/support-chat/${dm.userId}`) {
    return true;
  }
  if (dm.receiverUserId != null && pathname === `/support-chat/${dm.receiverUserId}`) {
    return true;
  }
  // A regular member's single thread lives at the bare route. Staff sitting on
  // the bare list aren't viewing a specific conversation, so they still toast.
  return !isStaff && pathname === '/support-chat';
}

export default function WebSocketWatcher() {
  const me = useMe();
  const meId = useRefUpdating(me?.id);
  const { urlPathname } = usePageContext();
  const currentAuthToken = useAuthToken();
  const idle = useIdleContext();
  // "Present" = on screen AND focused (`idle.present`, computed once in
  // useIdle.tsx). Deliberately narrower than `idle.inactive`: watching a live
  // release without moving the mouse trips the idle timer while the page is
  // plainly on screen and focused, and `inactive` would still read true. Every
  // notification/read-ack gate below must key on THIS ref, not `idle.inactive`
  // — see #2072 (the idle-timer gate raised an OS banner for every message
  // while the member was watching). The WS-activity/reconnect uses further
  // down read `idle.inactive` directly, which legitimately wants the idle
  // timer — leave those be.
  const presentRef = useRefUpdating(idle.present);
  const isOnline = useOnlineStatus();
  const knownIds = useRef<Set<API.Id>>(new Set());

  // `createdAt` of the newest receipt this client has considered for a tone
  // (#2041). Seeded with the moment the watcher mounted so a fresh page load
  // never replays tones for messages that were already posted and are sitting
  // right there on screen — only something posted after we started listening can
  // sound. It survives client-side navigation and backgrounding (this component
  // stays mounted in PageLayout), which is what lets a GO missed during a
  // WebSocket drop still sound on reconnect.
  const toneWatermark = useRef<number>(IS_SERVER ? 0 : Date.now());

  if (!IS_SERVER) {
    // iOS interval to make sure we keep sending silent notifications in an attempt to prevent background WS loss

    const platform = `${
      typeof navigator !== 'undefined' && (navigator.userAgentData?.platform || navigator.platform)
    }`;
    const isIos = platform.startsWith('i');

    // biome-ignore lint/correctness/useHookAtTopLevel: No need for SSR
    useInterval(
      () => {
        isIos &&
          openNotification(
            'RAS is running',
            'Keep this open for real-time notificaitons',
            '/release-center',
            'rasrtn',
            true,
          );
      },
      20 * 60 * 1000, // re-notify silently every 20 minutes
    );

    // biome-ignore lint/correctness/useHookAtTopLevel: No need for SSR
    useEffect(() => {
      isIos &&
        openNotification(
          'RAS is running',
          'Keep this open for real-time notificaitons',
          '/release-center',
          'rasrtn',
          true,
        );
    }, []);
  }

  // Sound readiness (#2041). The tones have to be decoded and the document has
  // to be audio-unlocked BEFORE the first GO, not at the moment it fires: a cold
  // `new Audio(cdnUrl).play()` both waits on a cross-origin fetch and is
  // rejected outright by the autoplay policy when the user hasn't interacted
  // with the page yet. So warm the buffers once we know who the user is, and
  // hook the unlock to the first gesture of the session.
  useEffect(() => {
    if (!me?.id) {
      return;
    }

    preloadSounds();
  }, [me?.id]);

  useEffect(() => installAudioUnlock(), []);

  const isStaff = me?.role === 'staff' || me?.role === 'admin';
  const isStaffRef = useRefUpdating(isStaff);

  const processMessageReceipt = useCallback((receipt: API.MessageReceipt) => {
    if (knownIds.current.has(receipt.id)) {
      return;
    }

    let stateToSet = FLAG_STATE.ACK;

    const isFromStaff =
      receipt.message?.user?.role === 'staff' || receipt.message?.user?.role === 'admin';
    const tone = classifyTone(receipt.message?.content, isFromStaff);

    const parsedCreatedAt = receipt.createdAt ? Date.parse(receipt.createdAt) : Number.NaN;
    const createdAtMs =
      Number.isFinite(parsedCreatedAt) && parsedCreatedAt ? parsedCreatedAt : null;

    // Decide the tone BEFORE the watermark moves, then move it. The watermark
    // tracks every receipt this client has CONSIDERED — not just the ones it
    // sounded — which is what makes the "never sound a stale GO over a newer
    // message" rule hold no matter what order a catch-up batch arrives in.
    const decision = decideTone({
      tone,
      createdAtMs,
      now: Date.now(),
      // The knownIds guard at the top of this callback is the de-dupe.
      alreadySounded: false,
      watermarkMs: toneWatermark.current,
    });

    if (createdAtMs !== null && createdAtMs > toneWatermark.current) {
      toneWatermark.current = createdAtMs;
    }

    const deliveredLate = decision.ageMs > TONE_LIVE_MS;

    // We don't use urlPathname so it prevents this callback from being re-bound on navigation
    const pathname = IS_SERVER ? urlPathname : location.pathname;
    const notOnPage = !pathname.startsWith('/release-center');

    // Gate on "present" (#2072), not the idle timer: a member watching a live
    // release without moving the mouse must not get a banner for a message
    // they're already looking at, even after the idle timer has tripped.
    if (!presentRef.current) {
      if (openNotification('RAS Release Center', receipt.message.content, '/release-center')) {
        stateToSet = FLAG_STATE.OPENED;
      }
    }

    // Not on releases page, but received a play message
    if (notOnPage) {
      stateToSet = FLAG_STATE.READ;

      dispatchShowAlert({
        id: `msg_${receipt.id}`,
        status: 'info',
        message: receipt.message.content,
        submessage: receipt.message.release?.playText,
        // Say so when a message reached this client well after it was posted, so
        // a banner that arrives without (or long after) its tone explains itself
        // rather than looking like a live message.
        pre: deliveredLate ? `Delivered ${formatLateness(decision.ageMs)} late` : undefined,
        href: '/release-center',
      });
    }

    // Bookkeeping now runs for EVERY receipt. The old 30s sound cutoff returned
    // from here, which also skipped the id de-dupe and the web-delivery flag —
    // so a late receipt was never recorded as delivered at all, understating
    // reach on exactly the receipts most worth knowing about.
    knownIds.current.add(receipt.id);

    // See if we need to flag the message as received by the client
    if (receipt.web < stateToSet) {
      webSocketWatcherEventTarget.dispatchEvent(
        new CustomEvent('ras:webSocketWatcher/flag', {
          detail: {
            event: `messageReceipt/flag`,
            data: {
              id: receipt.id,
              item: receipt,
              flag: stateToSet,
            },
          },
        }),
      );
    }

    if (knownIds.current.size > 20) {
      // Purge items to keep this list small
      for (let i = 10; i--; ) {
        const [first] = knownIds.current;
        knownIds.current.delete(first);
      }
    }

    switch (decision.outcome) {
      case 'play-live':
        // A live arrival also settles the slot: anything older still parked is
        // now stale and must not sound behind this one.
        if (createdAtMs !== null) {
          supersedeLateTone(createdAtMs);
        }
        void playSound(tone, { late: false, ageMs: decision.ageMs });
        break;

      case 'play-late':
        // Only reachable with a real timestamp: a receipt without one is judged
        // live, so this branch never sees a null.
        if (createdAtMs !== null) {
          scheduleLateTone(createdAtMs, tone, decision.ageMs);
        }
        break;

      case 'skip-late-ineligible':
        // Banner only — but it is still the newest thing we've seen, so it
        // clears any older tone waiting in the slot.
        if (createdAtMs !== null) {
          supersedeLateTone(createdAtMs);
        }
        reportSound('skipped', {
          name: tone,
          reason: decision.outcome,
          late: true,
          ageMs: decision.ageMs,
        });
        break;

      case 'skip-outside-window':
      case 'skip-superseded':
        reportSound('skipped', {
          name: tone,
          reason: decision.outcome,
          late: deliveredLate,
          ageMs: decision.ageMs,
        });
        break;

      default:
        break;
    }
  }, []);

  const createdCallback = useCallback(
    (
      event: CustomEvent<
        | EventItem<API.MessageReceipt, 'messageReceipt/created'>
        | EventItem<API.DirectMessageReceipt, 'directMessageReceipt/created'>
      >,
    ) => {
      const eventName = event.detail.event;

      if (eventName === 'messageReceipt/created') {
        processMessageReceipt(event.detail.data);
      } else if (eventName === 'directMessageReceipt/created') {
        // Gate on "present" (#2072) — see presentRef above. `openNotification`
        // below always prefers the service worker when one is registered
        // (installed PWA or not), so this single gate also covers the SW-routed
        // banner; there's no separate PWA-only call site to guard.
        if (!presentRef.current) {
          if (event.detail.data.userId === meId.current) {
            // Never notify me of a message I sent myself (#1738). On an installed
            // PWA openNotification below fires through the service worker
            // (registration.showNotification), so a missing guard here surfaces as
            // a "web push for my own message". A prior refactor (0f3d4934) dropped
            // the `return`, leaving this an empty no-op — restore it.
            return;
          }
          if (
            isStaffRef.current &&
            (!event.detail.data.userId || event.detail.data.receiverUserId)
          ) {
            // I'm staff/admin and this is an admin-authored message (admin -> user,
            // or has no sender). I'm not the recipient of it, so don't notify me —
            // the recipient user is pushed separately server-side (#1738).
            return;
          }
          if (
            openNotification(
              'RAS Support',
              event.detail.data.content,
              isStaffRef.current && event.detail.data.userId
                ? `/support-chat/${event.detail.data.userId}`
                : '/support-chat',
            )
          ) {
            // @todo use processMessageReceipt so we can send message acks
            // stateToSet = FLAG_STATE.OPENED;
          }
        }
      }
    },
    [processMessageReceipt],
  );

  const messageReceiptFlagRequest: RequestSchema<
    API.DirectMessageReceipt,
    API.WithData<{ type: 'message' | 'dm'; web: number }>
  > = useMemo(
    () => ({
      name: 'messageReceiptFlagRequest',
      input: `/api/v1/messageReceipt/me/:id/flag`,
      method: 'POST',
    }),
    [],
  );

  const updateMessageRequest = useRequestFetcher(messageReceiptFlagRequest, { manual: true });

  const flagCallback = useCallback(
    (
      event: CustomEvent<
        | EventFlag<API.MessageReceipt, 'messageReceipt/flag'>
        | EventFlag<API.DirectMessageReceipt, 'directMessageReceipt/flag'>
      >,
    ) => {
      const eventName = event.detail.event;
      const itemId = event.detail.data.id || event.detail.data.item?.id;
      const idleFlag = event.detail.data.idleFlag;
      // #2072: keyed on `presentRef`, not `idle.inactive`, for the same reason
      // as the notification gates above. `idleFlag` is only ever sent by a
      // dispatcher that has already decided the member is not reading this
      // right now — release-center's MessageReceiptLogger keys that decision on
      // `idle.present` (#1885/#2058), so `!present` is guaranteed true whenever
      // `idleFlag` is set for that path and this check is a no-op there.
      // Evaluated separately per #2072: this also covers the DM read-ack
      // dispatcher in DMReceiptList.tsx, which still keys its OWN decision on
      // the broader idle timer rather than presence (a related but distinct
      // gap the DMReceiptList downgrade logic still needs). For that path this
      // makes the write MORE correct, not less: a DM that arrives while the
      // member is present-but-idle now writes straight to VISITED here instead
      // of being downgraded to ACK, matching the "present, not merely
      // non-idle" rule everywhere else. DMReceiptList's own local `webStatus`
      // bookkeeping still starts at ACK in that case, but its retry loop
      // promotes it to VISITED on the next pass (idempotent with the write this
      // produces), so nothing is left stranded.
      const nextFlag = idleFlag && !presentRef.current ? idleFlag : event.detail.data.flag;

      if (!itemId) {
        return;
      }

      // Message receipts (the release fan-out, #1801): coalesce a burst into ONE
      // bulk write instead of one request per receipt id.
      if (eventName === 'messageReceipt/flag') {
        queueWebFlag(itemId, nextFlag);
        return;
      }

      // DMs are 1:1 and never fan out like a release, so they keep the simple
      // per-id monotonic path.
      const cache = dmFlagCacheMap;
      const id = `${itemId}`;
      const prev = cache.get(id);
      const cb = () =>
        updateMessageRequest.run(
          { data: { type: 'dm', web: nextFlag } },
          {
            req: { input: messageReceiptFlagRequest.input.toString().replace(':id', id) },
            dontAlterState: true,
            dontStoreError: true,
          },
        );

      if (prev) {
        // If we already had a flag pending for this one, queue up a new one if the new flag is higher
        if (prev.state < nextFlag) {
          clearTimeout(prev.timer);

          cache.set(id, {
            state: nextFlag,
            timer: setTimeout(cb, 500 + JITTER),
          });
        }
      } else {
        cache.set(id, {
          state: nextFlag,
          timer: setTimeout(cb, 500 + JITTER),
        });
      }

      // Clean up
      setTimeout(
        () => {
          if (cache.size > 30) {
            for (const [key] of cache) {
              cache.delete(key);

              if (cache.size <= 30) {
                break;
              }
            }
          }
        },
        (500 + JITTER) * 4,
      );
    },
    [],
  );

  useEffect(() => {
    // Only happens client-side
    webSocketWatcherEventTarget.addEventListener(
      'ras:webSocketWatcher/created',
      createdCallback,
      false,
    );
    webSocketWatcherEventTarget.addEventListener('ras:webSocketWatcher/flag', flagCallback, false);

    return () => {
      webSocketWatcherEventTarget.removeEventListener(
        'ras:webSocketWatcher/created',
        createdCallback,
        false,
      );
      webSocketWatcherEventTarget.removeEventListener(
        'ras:webSocketWatcher/flag',
        flagCallback,
        false,
      );
    };
  }, [createdCallback, flagCallback]);

  const onWatcherCreated = useCallback(
    (_event: MessageEvent, response: API.SubscriptionResponse<API.MessageReceipt>) =>
      processMessageReceipt(response.data),
    [processMessageReceipt],
  );

  const onWatcherDmCreated = useCallback(
    (_event: MessageEvent, json: API.SubscriptionResponse<API.DirectMessageReceipt>) => {
      // We don't use urlPathname so it prevents this callback from being re-bound on navigation
      const pathname = IS_SERVER ? urlPathname : location.pathname;
      let dmPath = '/support-chat';
      let submessage = json.data.user?.firstName || 'User';
      let chatId: string | number = 'me';

      if (isStaffRef.current) {
        if (!json.data.receiverUserId) {
          chatId = json.data.userId; // msg from user
          submessage = `${submessage} ←`;
        } else if (json.data.receiverUserId) {
          chatId = json.data.receiverUserId; // msg from admin
          submessage = `${submessage} (RAS) → ${
            json.data.receiverUser?.firstName || json.data.receiverUserId
          }`;
        }

        dmPath = `${dmPath}/${chatId}`;

        if (json.data.receiverUserId) {
          // When msg from admin to user, clear the existing notification if any (we don't need to attend to it anymore)
          clearPageNotifications(dmPath);
          dispatchRemoveAlert(`dm_${chatId}`);

          return;
        }
      }

      // Not already viewing this conversation, so surface the DM as a toast. This
      // is gated on the pathname vs. the DM's participant ids (not `dmPath`, which
      // depends on the role/direction classification above) so an admin sitting in
      // the conversation never gets a toast for a message already on their screen,
      // even if `isStaff` was momentarily unset when the event arrived.
      if (!viewingDmConversation(pathname, json.data, isStaffRef.current)) {
        dispatchShowAlert({
          id: `dm_${chatId}`,
          status: 'info',
          message: json.data.content,
          submessage,
          href: dmPath,
          uId: json.data.userId,
          uFirst: json.data.user?.firstName,
          uLast: json.data.user?.lastName,
          uRole: json.data.user?.role,
          uAvatarUrl: json.data.user?.avatarUrl,
        });
      }

      // Gate on "present" (#2072), not the idle timer — see presentRef above.
      if (!presentRef.current) {
        openNotification('RAS Support', json.data.content, dmPath);
      }

      // 1647 is Cleatus
      if (dmPath === '/support-chat/1647') {
        return;
      }

      if (
        (!isStaffRef.current && json.data.receiverUserId) ||
        !viewingDmConversation(pathname, json.data, isStaffRef.current)
      ) {
        // Regular users always get DM sound receipts, admins only when not on that DM page
        void playSound('pop');
      }
    },
    [],
  );

  const onWatcherClose = useCallback((_event: Event) => {
    // @see PageTokenRefresher: the event.reason === 401, 402, 403 check which sets authToken(null) is not here
  }, []);

  const onWatcherReady = useCallback((event: MessageEvent) => {
    const data: ConnectData = JSON.parse(event.data);

    // The native (Capacitor) app's bundle is baked in and only updates through
    // the App Store, so "you're out of date — reload" can't fix a mismatch on
    // native; it just loops (the server deploys independently of app releases).
    // Only prompt on the web, where a reload actually fetches the new bundle.
    const isNative = !!(
      window as unknown as { Capacitor?: { isNativePlatform?: () => boolean } }
    ).Capacitor?.isNativePlatform?.();

    // A bare `appDate` mismatch no longer prompts (#1680). Almost every backend
    // deploy is backwards-compatible (additive endpoints, flag-gated features,
    // internal logic, perf, bug fixes) and bumps the server appDate without any
    // client/server contract break, so prompting on `appDate > APP_DATE`
    // over-nagged on every routine deploy. Such deploys now apply silently on
    // the user's next navigation — the same treatment client-only deploys got
    // in #1672.
    //
    // We force a reload only when this client is below the server's
    // CLIENT_MIN_DATE floor — the exact condition the API's 417 gate uses
    // (core-middleware compares CLIENT_MIN_DATE against the `x-ras-cd` header,
    // which is this same APP_DATE). CLIENT_MIN_DATE is bumped only for a
    // genuinely breaking client/server contract change, and is unset (0) in
    // prod today, so this path stays quiet until such a deploy. The 417 gate
    // already enforces the same floor on the next API request; this just
    // surfaces the prompt immediately on (re)connect instead of waiting.
    const clientMinDate = data?.versions?.clientMinDate || 0;

    if (!isNative && APP_DATE && clientMinDate && APP_DATE < clientMinDate) {
      dispatchOutdatedAlert();
    }
  }, []);

  const onWatcherMessage = useCallback(function (
    this: Connection,
    event: MessageEvent,
    _subscriber:
      | API.SubscriptionResponse<API.MessageReceipt | API.DirectMessageReceipt, string>
      | null
      | undefined,
  ) {
    if (event.data === 'PONG') {
      // Server keepalive PONG — the connection layer tracks liveness; nothing
      // to do here, just don't treat it as a message payload.
      return;
    }

    if (event.data.startsWith('{"message":"UNAUTHORIZED"')) {
      // not allowed; clear queue and disconnect
      authToken(null);
    } else if (event.data.startsWith('{"message":"EXPIRED"')) {
      // refresh token and reconnect
      this.close();
      refreshToken().then(() => this.reconnect());
    }
  }, []);

  const onWatcherAuthLogout = useCallback(
    (_event: MessageEvent, json: API.SubscriptionResponse<{ code: string; message: string }>) => {
      dispatchShowAlert({
        id: `auth_logout`,
        status: 'info',
        message:
          (json.data.code === 'AUTH/LOGOUT:SESSION_LIMIT'
            ? 'You have logged in from too many devices, and have been logged out from this one.'
            : json.data.message) || 'You have been logged out from another location.',
        // submessage: receipt.message.release?.playText,
        href: '/signin',
        // permanent: true,
        duration: 60 * 60 * 24 * 7 * 1000, // this is better than permanent, as it is dismissable
      });
    },
    [],
  );

  useUpdateEffect(() => {
    if (me?.id) {
      dispatchRemoveAlert('auth_logout');
    }
  }, [me?.id]);

  const { ready } = useWebSocket(WS_BASE_URL, {
    onClose: onWatcherClose,
    onMessage: onWatcherMessage,
    onReady: onWatcherReady,
    subscriptions: {
      'messageReceipt/created': onWatcherCreated,
      'directMessageReceipt/created': onWatcherDmCreated,
      'auth/logout': onWatcherAuthLogout,
    },
    connect: !!currentAuthToken,
  });

  // Tell the server whether the user is actively watching (foreground + focused
  // + not idle) so it can suppress a redundant DM push while they're present and
  // let it through once they minimize/background. Re-sent whenever readiness or
  // activity changes — the readiness dependency covers reconnects, which the
  // server starts as active until it hears otherwise.
  useEffect(() => {
    if (ready) {
      setWsActivity(!idle.inactive);
    }
  }, [ready, idle.inactive]);

  const [showWarning, setShowWarning] = useState(WarningLevel.None);

  const isFirstRender = useIsFirstRender();
  const { isBrowser } = useSsr();

  // If we haven't been able to connect to the WS recently, show the delayed message
  const timeoutShort = useTimeout(
    () => {
      if (isBrowser) {
        setShowWarning(WarningLevel.Short);
      }
    },
    !ready && currentAuthToken ? (isFirstRender ? WAIT_START : WAIT_SHORT) : null,
  );

  // If we haven't been able to connect to the WS for over (WAIT_LONG), prompt them to refresh.
  // Paused while the user is inactive (tab hidden, no focus, idle) so the timer only counts
  // down against time the user is actively watching the page. The delay dependency resets the
  // clock whenever inactive→active transitions happen, giving a clean 2-minute window each time.
  const timeoutLong = useTimeout(
    () => {
      if (isBrowser) {
        setShowWarning(WarningLevel.Long);
      }
    },
    !ready && currentAuthToken && !idle.inactive ? WAIT_LONG : null,
  );

  useEffect(() => {
    if (ready) {
      clearInterval(timeoutShort.current as number);
      clearInterval(timeoutLong.current as number);
      setShowWarning(WarningLevel.None);
    }
  }, [ready]);

  const rasContext = useRasContext();

  // RasContext ConnectionStatus handler begins
  const networkState = useNetworkState();

  const baseConnectionStatus = useMemo(() => {
    if (!IS_SERVER) {
      if (networkState.online) {
        if (networkState.effectiveType === '2g' || networkState.effectiveType === 'slow-2g') {
          return ConnectionStatus.SlowCellular;
        } else {
          return ConnectionStatus.Stable;
        }
      } else {
        return ConnectionStatus.Offline;
      }
    }
    return ConnectionStatus.Loading;
  }, [networkState.effectiveType, networkState.online]);

  const recentDisconnectionCountRef = useRef(0);
  const lastCountCheckRef = useRef(0);

  useEffect(() => {
    if (showWarning === WarningLevel.Short) {
      recentDisconnectionCountRef.current++;
    }
  }, [showWarning]);

  useEffect(() => {
    if (baseConnectionStatus === ConnectionStatus.Offline) {
      // Offline overrides all else
      rasContext.setData?.('connectionStatus', baseConnectionStatus);
    } else if (showWarning === WarningLevel.Long) {
      // If we lost WS has been long disconnected, consider WS unstable
      rasContext.setData?.('connectionStatus', ConnectionStatus.WebsocketDisconnected);
    } else if (showWarning === WarningLevel.Short) {
      // WS currently disconnected, possibly temporarily
      rasContext.setData?.('connectionStatus', ConnectionStatus.WebsocketDisconnected);
    } else if (recentDisconnectionCountRef.current > 2) {
      // If we lost WS connection twice in the last 60s, consider WS unstable
      rasContext.setData?.('connectionStatus', ConnectionStatus.WebsocketUnstable);
    } else {
      rasContext.setData?.('connectionStatus', baseConnectionStatus);
    }
    // @todo handle ConnectionStatus.PollingUnstable
  }, [showWarning, rasContext.setData, baseConnectionStatus]);

  useInterval(() => {
    // Only reset count if it hasn't changed in 60s
    if (lastCountCheckRef.current === recentDisconnectionCountRef.current) {
      recentDisconnectionCountRef.current = 0;
    }

    lastCountCheckRef.current = recentDisconnectionCountRef.current;
  }, 60000);
  // RasContext ConnectionStatus handler ends

  if (
    currentAuthToken &&
    showWarning === WarningLevel.Long /* || showWarning === WarningLevel.Short*/
  ) {
    return (
      <>
        <aside className="sticky top-0 bg-amber-50 p-4 dark:bg-amber-500/10">
          <div className="flex flex-row items-start space-x-2 text-sm text-amber-700 dark:text-amber-300">
            <SignalSlashIcon height={24} className="size-6 shrink-0" />
            <div className="flex flex-col space-x-2">
              {showWarning === WarningLevel.Long
                ? isOnline
                  ? 'Reconnecting… your live updates may be delayed. Tap Refresh if this keeps happening. '
                  : 'Your internet connection appears to be offline. '
                : `Degraded connection. Messaging service may be delayed.`}
              {showWarning === WarningLevel.Long && (
                <a
                  href="#"
                  className="mt-3 inline-block w-fit rounded px-2.5 py-1.5 text-amber-700 ring-1 ring-amber-700 hover:bg-amber-700/5 hover:text-amber-800 dark:text-amber-300 dark:ring-amber-300 dark:hover:bg-amber-300/5 dark:hover:text-amber-400"
                  onClick={(event) => {
                    event.preventDefault();
                    location.reload();
                  }}
                >
                  Refresh
                </a>
              )}
            </div>
          </div>
        </aside>
      </>
    );
  }

  return null;
}
